import React from 'react';
import { Link } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Eye, Pencil, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatRial } from '@/lib/formatMoney';
import { accountingDocumentHref } from '@/lib/accountingDocumentProjectScope';
import type { AccountingDocumentRecord } from '@/lib/api/accounting';
import type { GroupedProjectDocumentRow } from '@/lib/accountingDocumentFilters';

function DiffCell({ difference }: { difference: number }) {
  const diff = Number(difference || 0);
  return (
    <span className={diff === 0 ? 'font-mono text-emerald-700' : 'font-mono text-rose-700'}>
      {formatRial(Math.abs(diff))}
      {diff !== 0 ? <span className="ms-1 text-[10px]">نامتراز</span> : null}
    </span>
  );
}

export function createAccountingDocumentListColumns(): ColumnDef<AccountingDocumentRecord>[] {
  return [
    {
      accessorKey: 'docNo',
      header: 'شماره سند',
      cell: ({ row }) => (
        <span className="whitespace-nowrap font-mono text-sm font-medium" dir="ltr">
          {row.original.docNo}
        </span>
      ),
    },
    {
      accessorKey: 'docDate',
      header: 'تاریخ',
      cell: ({ row }) => (
        <span className="whitespace-nowrap font-mono text-sm" dir="ltr">
          {row.original.docDate}
        </span>
      ),
    },
    {
      accessorKey: 'operationDescription',
      header: 'شرح عملیات',
      cell: ({ row }) => (
        <span className="line-clamp-2 max-w-[20rem] text-sm text-slate-700" title={row.original.operationDescription}>
          {row.original.operationDescription || '—'}
        </span>
      ),
    },
    {
      id: 'projects',
      header: 'پروژهٔ مرتبط',
      cell: ({ row }) => {
        const labels = row.original.projectLabels || [];
        if (!labels.length) return <span className="text-slate-400">—</span>;
        return (
          <span className="line-clamp-2 text-xs text-slate-600" title={labels.join(' · ')}>
            {labels.join(' · ')}
          </span>
        );
      },
    },
    {
      accessorKey: 'totalDebit',
      header: 'بدهکار',
      cell: ({ row }) => (
        <span className="whitespace-nowrap font-mono text-sm text-rose-800">
          {formatRial(Number(row.original.totalDebit || 0))}
        </span>
      ),
    },
    {
      accessorKey: 'totalCredit',
      header: 'بستانکار',
      cell: ({ row }) => (
        <span className="whitespace-nowrap font-mono text-sm text-emerald-800">
          {formatRial(Number(row.original.totalCredit || 0))}
        </span>
      ),
    },
    {
      accessorKey: 'difference',
      header: 'اختلاف',
      cell: ({ row }) => <DiffCell difference={Number(row.original.difference || 0)} />,
    },
    {
      id: 'actions',
      header: () => <div className="text-center">عملیات</div>,
      cell: ({ row }) => (
        <div className="flex justify-center gap-1">
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" asChild title="مشاهده">
            <Link to={`/accounting-documents/${row.original.id}`}>
              <Eye className="h-4 w-4" />
            </Link>
          </Button>
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-indigo-700" asChild title="ویرایش">
            <Link to={`/accounting-documents/${row.original.id}/edit`}>
              <Pencil className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      ),
    },
  ];
}

export function createProjectDocumentListColumns(
  projectId?: number,
): ColumnDef<GroupedProjectDocumentRow>[] {
  return [
    {
      accessorKey: 'docNo',
      header: 'شماره سند',
      cell: ({ row }) => (
        <span className="whitespace-nowrap font-mono text-sm font-medium" dir="ltr">
          {row.original.docNo}
        </span>
      ),
    },
    {
      accessorKey: 'dateFa',
      header: 'تاریخ',
      cell: ({ row }) => (
        <span className="whitespace-nowrap font-mono text-sm" dir="ltr">
          {row.original.dateFa}
        </span>
      ),
    },
    {
      accessorKey: 'operationDescription',
      header: 'شرح عملیات',
      cell: ({ row }) => (
        <span className="line-clamp-2 max-w-[18rem] text-sm text-slate-700" title={row.original.operationDescription}>
          {row.original.operationDescription || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'beneficiaries',
      header: 'طرف حساب',
      cell: ({ row }) => (
        <div className="flex max-w-[14rem] items-start gap-2 text-sm">
          <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
          <span className="line-clamp-2 font-medium text-slate-700" title={row.original.beneficiaries}>
            {row.original.beneficiaries || '—'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'lineCount',
      header: 'تعداد ردیف',
      cell: ({ row }) => <span className="font-mono text-sm">{row.original.lineCount}</span>,
    },
    {
      accessorKey: 'totalDebit',
      header: 'بدهکار',
      cell: ({ row }) => (
        <span className="whitespace-nowrap font-mono text-sm text-rose-800">
          {formatRial(row.original.totalDebit)}
        </span>
      ),
    },
    {
      accessorKey: 'totalCredit',
      header: 'بستانکار',
      cell: ({ row }) => (
        <span className="whitespace-nowrap font-mono text-sm text-emerald-800">
          {formatRial(row.original.totalCredit)}
        </span>
      ),
    },
    {
      id: 'difference',
      header: 'اختلاف',
      cell: ({ row }) => <DiffCell difference={row.original.difference} />,
    },
    {
      id: 'actions',
      header: () => <div className="text-center">عملیات</div>,
      cell: ({ row }) => (
        <div className="flex justify-center gap-1">
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" asChild title="مشاهده">
            <Link to={accountingDocumentHref(row.original.documentId, { projectId })}>
              <Eye className="h-4 w-4" />
            </Link>
          </Button>
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-indigo-700" asChild title="ویرایش">
            <Link to={accountingDocumentHref(row.original.documentId, { projectId, edit: true })}>
              <Pencil className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      ),
    },
  ];
}
